import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Markdown } from "@/components/Markdown";
import { cn } from "@/lib/utils";

const COLLAPSED = "max-h-44";

export function TemplatePreview({ name, content }: { name: string; content: string }) {
  const clipRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [clipped, setClipped] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (expanded) return;
    const clip = clipRef.current;
    const inner = contentRef.current;
    if (!clip || !inner) return;
    const measure = () => setClipped(clip.scrollHeight > clip.clientHeight + 1);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(inner);
    return () => observer.disconnect();
  }, [content, expanded]);

  return (
    <div className="flex flex-col items-start gap-1">
      <div ref={clipRef} className={cn("relative w-full overflow-hidden", !expanded && COLLAPSED)}>
        <div ref={contentRef}>
          <Markdown
            source={content}
            className="text-slate-500 [&_h1]:mt-3 [&_h1]:text-base [&_h2]:mt-3 [&_h2]:text-sm [&_h3]:mt-3 [&_h3]:text-sm [&_h4]:text-xs [&_h5]:text-xs"
          />
        </div>
        {clipped && !expanded && (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-card" />
        )}
      </div>
      {clipped && (
        <Button
          type="button"
          variant="link"
          size="sm"
          className="h-auto p-0"
          aria-expanded={expanded}
          aria-label={`${expanded ? "Show less" : "Show more"} of ${name}`}
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? "Show less" : "Show more"}
        </Button>
      )}
    </div>
  );
}
